import { useEffect } from 'react';
import { useRouter } from 'next/router';
import { API_BASE } from './api-base';
import { ensureMe } from './me';
import { paidActivePlanIds, type PickerPremium } from './plans';
import { premiumFetch } from './premium-fetch';
import { useBasePath } from './use-base-path';
import { withUpgradeOrigin } from './upgrade-origin';
import {
  type AffiliateArrival,
  affiliatePartnerFromReferrer,
  clearAffiliateArrival,
  loadAffiliateArrival,
  saveAffiliateArrival,
} from './affiliate-arrival';

// The affiliate flow described in `affiliate-arrival.ts`. Web only, by construction — see orion-web/CLAUDE.md.

/** Bounds the lookups on the way out of signup and checkout, so a hung request must not strand the user. */
const LOOKUP_TIMEOUT_MS = 3000;
/** Looser: the visitor waits on the home page meanwhile, and a wrong answer sends a paying user to the picker. */
const PLAN_LOOKUP_TIMEOUT_MS = 10000;

function withinTimeout<T>(work: Promise<T>, fallback: T, timeoutMs = LOOKUP_TIMEOUT_MS): Promise<T> {
  return Promise.race([work, new Promise<T>((resolve) => setTimeout(() => resolve(fallback), timeoutMs))]);
}

async function fetchAffiliateLandingUrl(partner: string): Promise<string | null> {
  const res = await fetch(`${API_BASE}/api/affiliate/landing?partner=${encodeURIComponent(partner)}`, {
    credentials: 'include',
    signal: AbortSignal.timeout(LOOKUP_TIMEOUT_MS),
  });
  if (!res.ok) throw new Error(`affiliate landing lookup failed: ${res.status}`);
  const body = (await res.json()) as { url?: string | null };
  return body.url ?? null;
}

async function hasPaidPlan(): Promise<boolean> {
  const res = await premiumFetch(`${API_BASE}/api/premium`);
  if (!res.ok) throw new Error(`premium lookup failed: ${res.status}`);
  const body = (await res.json()) as { premium?: PickerPremium | null };
  return paidActivePlanIds(body.premium ?? null).size > 0;
}

/** The account write in flight, so spending the arrival never races ahead of it (`leaveForAffiliateLanding`). */
let accountWrite: Promise<void> | null = null;

/**
 * Keeps the arrival on the user's own recipient too, for a purchase made in another browser. Best-effort, and
 * sent on every visit to the picker: the server ignores a capture it already holds, so a failed save is
 * simply retried by the next visit.
 */
function rememberArrivalOnAccount({ partner, capturedAt }: AffiliateArrival): void {
  accountWrite = fetch(`${API_BASE}/api/affiliate/arrival`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ partner, capturedAt }),
    signal: AbortSignal.timeout(LOOKUP_TIMEOUT_MS),
  }).then(
    (res) => {
      if (!res.ok) console.warn(`[affiliate-redirect] could not keep the arrival on the account: ${res.status}`);
    },
    (err) => {
      console.warn('[affiliate-redirect] could not keep the arrival on the account', err);
    },
  );
}

/**
 * Spends the account copy and answers its landing page. `keepalive`, because the browser-copy path calls it
 * without waiting, right before navigating away.
 */
async function claimAccountLandingUrl(): Promise<string | null> {
  const res = await fetch(`${API_BASE}/api/affiliate/claim`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: '{}',
    keepalive: true,
    signal: AbortSignal.timeout(LOOKUP_TIMEOUT_MS),
  });
  if (!res.ok) throw new Error(`affiliate claim failed: ${res.status}`);
  const body = (await res.json()) as { url?: string | null };
  return body.url ?? null;
}

/** `fromBrowser` is false when the landing came from the account copy, which the claim already spent. */
interface AffiliateLanding {
  url: string;
  fromBrowser: boolean;
}

/**
 * The landing page for this user's affiliate arrival, or null when there is none. An arrival whose affiliate
 * has no configured page is dropped, so switching an affiliate off in config leaves its visitors untouched.
 * With `fromAccount`, a browser with no arrival of its own falls back to the account copy: only after a
 * confirmed payment, so signup and arrival never pay for the extra request.
 */
async function currentAffiliateLandingUrl({ fromAccount = false } = {}): Promise<AffiliateLanding | null> {
  const userId = (await withinTimeout(ensureMe(), null))?.userId ?? '';
  const arrival = loadAffiliateArrival(userId);
  if (!arrival) {
    const url = fromAccount ? await claimAccountLandingUrl() : null;
    return url ? { url, fromBrowser: false } : null;
  }
  const url = await fetchAffiliateLandingUrl(arrival.partner);
  if (!url) clearAffiliateArrival();
  return url ? { url, fromBrowser: true } : null;
}

async function leaveForAffiliateLanding({ url, fromBrowser }: AffiliateLanding): Promise<void> {
  clearAffiliateArrival();
  if (fromBrowser) {
    // Spend the account copy too, or a later purchase in another browser is redirected again. After any write
    // still in flight, so the claim finds the copy that write saved.
    await accountWrite;
    void claimAccountLandingUrl().catch((err) => {
      console.warn('[affiliate-redirect] could not spend the account copy of the arrival', err);
    });
  }
  // `replace`, so Back does not return the user into a finished signup or checkout.
  window.location.replace(url);
}

/** Whether a just-created account goes to the plan picker instead of home. */
export async function shouldSendAffiliateToUpgrade(): Promise<boolean> {
  try {
    return (await withinTimeout(currentAffiliateLandingUrl(), null)) !== null;
  } catch (err) {
    console.warn('[affiliate-redirect] could not decide the upgrade redirect; continuing without it', err);
    return false;
  }
}

/**
 * Mounted from `_app`: catches the affiliate off the first page load. A visitor who is already a Symphony
 * user lands on `/`: one with a paid plan goes straight to the affiliate's page, anyone else to the plan
 * picker. Only on that arrival — a later visit carries no `referrer`. A new user lands on `/welcome`
 * instead, and `CreateRecipientOnEnter` sends them once their account exists.
 */
export function useAffiliateArrival(): void {
  const router = useRouter();
  const basePath = useBasePath();
  useEffect(() => {
    const partner = affiliatePartnerFromReferrer(new URLSearchParams(window.location.search).get('referrer'));
    if (!partner) return;
    const unbound: AffiliateArrival = { partner, userId: '', capturedAt: Date.now() };
    saveAffiliateArrival(unbound);
    const landedOnHome = router.pathname === '/';

    let cancelled = false;
    void (async () => {
      const userId = (await withinTimeout(ensureMe(), null))?.userId;
      if (cancelled || !userId) return;
      saveAffiliateArrival({ ...unbound, userId });
      if (!landedOnHome) return;
      const landing = await currentAffiliateLandingUrl();
      if (!landing || cancelled) return;
      if (await withinTimeout(hasPaidPlan(), false, PLAN_LOOKUP_TIMEOUT_MS)) await leaveForAffiliateLanding(landing);
      // A full load, not a route change: a returner mid-onboarding lands here with the chat fullscreen
      // (`?chat=fullscreen`), and a route change would keep it on top of the picker.
      else window.location.replace(withUpgradeOrigin(`${basePath}/packages`, 'arrival_redirect'));
    })().catch((err) => {
      console.warn('[affiliate-redirect] could not route the affiliate visitor; leaving them where they landed', err);
    });
    return () => {
      cancelled = true;
    };
    // Reads the landing URL once, on the first load: later navigations carry no `referrer`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}

/**
 * Mounted on the plan picker: an affiliate buyer goes to the affiliate's landing page once the payment is
 * confirmed. Keyed on the confirmed outcome rather than on the plan alone, so the page's purchase
 * analytics, which run before that outcome is set, are never cut off by the navigation.
 *
 * Every affiliate buyer passes through the picker before paying, so this is also where the browser's arrival
 * is copied onto the account: by now the account surely exists, which right after signup it may not yet.
 */
export function useAffiliateLandingRedirect(justPaid: boolean): void {
  useEffect(() => {
    void (async () => {
      // A known user only: without one, `loadAffiliateArrival` cannot reject another user's arrival, and this
      // would copy it onto the account of whoever is signed in.
      const userId = (await withinTimeout(ensureMe(), null))?.userId;
      const arrival = userId ? loadAffiliateArrival(userId) : null;
      if (arrival) rememberArrivalOnAccount(arrival);
    })();
  }, []);

  useEffect(() => {
    if (!justPaid) return;
    void currentAffiliateLandingUrl({ fromAccount: true })
      .then((landing) => (landing ? leaveForAffiliateLanding(landing) : undefined))
      .catch((err) => {
        console.warn('[affiliate-redirect] could not send the buyer to the affiliate landing page', err);
      });
  }, [justPaid]);
}
